import { useEffect, useState } from "react";
import { Search, Plus, X } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { fmtNaira, fmtDate } from "@/lib/api";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setUsers(r.data.users || []);
    } catch (_) {
      // 401/403 → global interceptor redirects to /admin/login.
      // Other errors: keep the current list, just release the spinner.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Traders</h1>
          <p className="text-sm text-kudi-ink/60">{users.length} users</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kudi-ink/40" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, ref code..."
            className="bg-white border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-kudi-green/20 focus:border-kudi-green"
            data-testid="user-search"
          />
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent/40 text-xs uppercase tracking-wider text-kudi-ink/60">
              <tr>
                <th className="text-left px-4 py-3">Trader</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Business</th>
                <th className="text-right px-4 py-3">Transactions</th>
                <th className="text-right px-4 py-3">Sales</th>
                <th className="text-left px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-kudi-ink/40">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-kudi-ink/40">No traders yet</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} onClick={() => setSelected(u.id)} className="hover:bg-accent/30 cursor-pointer" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-kudi-green to-kudi-terracotta text-white text-xs font-heading font-bold flex items-center justify-center">
                        {u.owner_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="font-medium">{u.owner_name || "—"}</div>
                        {u.referral_code && <div className="text-[10px] font-mono text-kudi-ink/40">{u.referral_code}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-kudi-ink/70 text-xs">
                    <div className="truncate max-w-[200px]">{u.email || "—"}</div>
                    {u.phone && <div className="font-mono text-[10px] text-kudi-ink/40">{u.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-kudi-ink/70">{u.business_name || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{u.tx_count || 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtNaira(u.total_sales || 0)}</td>
                  <td className="px-4 py-3 text-kudi-ink/50 text-xs">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <UserDetailDrawer userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function UserDetailDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [grantAmount, setGrantAmount] = useState("");

  useEffect(() => { adminApi.get(`/admin/users/${userId}`).then((r) => setData(r.data)); }, [userId]);

  const grantCredit = async () => {
    const amt = Number(grantAmount);
    if (!amt || amt <= 0) { toast.error("Enter amount"); return; }
    try {
      const r = await adminApi.post(`/admin/users/${userId}/grant-credit`, { amount: amt, reason: "Admin grant" });
      toast.success(`Granted ${fmtNaira(amt)}. New credit: ${fmtNaira(r.data.new_loan_credit)}`);
      setGrantAmount("");
      adminApi.get(`/admin/users/${userId}`).then((r2) => setData(r2.data));
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-detail-drawer">
        <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Trader Detail</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        {!data ? <div className="p-8 text-center text-kudi-ink/50">Loading...</div> : (
          <div className="p-5 space-y-5">
            <div>
              <div className="font-heading text-xl font-bold">{data.user.owner_name}</div>
              <div className="text-sm text-kudi-ink/60">{data.user.business_name} · {data.user.business_type}</div>
              <div className="text-xs text-kudi-ink/40 mt-1">📍 {data.user.location}</div>
              <div className="text-xs text-kudi-ink/60 mt-1 truncate">✉️ {data.user.email || "—"}</div>
              {data.user.phone && <div className="text-xs text-kudi-ink/40 font-mono">📞 {data.user.phone}</div>}
              {data.user.referral_code && <div className="text-xs text-kudi-ink/40 font-mono mt-1">Ref code: {data.user.referral_code}</div>}
            </div>

            <div className="bg-accent rounded-xl p-4">
              <div className="text-xs font-semibold text-kudi-ink/70 uppercase tracking-wider mb-2">Grant loan credit</div>
              <div className="flex gap-2">
                <input type="number" value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} placeholder="₦ amount" className="flex-1 bg-white rounded-lg px-3 py-2 text-sm" data-testid="grant-amount" />
                <button onClick={grantCredit} className="bg-kudi-green text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-kudi-green-dark transition flex items-center gap-1" data-testid="grant-btn"><Plus className="w-3.5 h-3.5" /> Grant</button>
              </div>
              <div className="text-xs text-kudi-ink/55 mt-2">Current loan credit: {fmtNaira(data.user.loan_credit || 0)}</div>
            </div>

            <Section title={`Loans (${data.loans.length})`}>
              {data.loans.length === 0 ? <Empty>No loans</Empty> : data.loans.map((l) => (
                <div key={l.id} className="bg-white border border-border rounded-lg p-3 mb-2 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">{fmtNaira(l.amount)}</span><Badge status={l.transfer_status} /></div>
                  <div className="text-xs text-kudi-ink/55 mt-1">{l.bank_name} · {l.account_name} · {l.account_number}</div>
                  <div className="text-xs text-kudi-ink/55">Repaid {fmtNaira(l.amount_repaid)} / {fmtNaira(l.total_repayment)}</div>
                </div>
              ))}
            </Section>

            <Section title={`Recent transactions (${data.transactions.length})`}>
              {data.transactions.length === 0 ? <Empty>None</Empty> : data.transactions.slice(0, 15).map((t) => (
                <div key={t.id} className="flex justify-between text-sm py-2 border-b border-border/40">
                  <div>
                    <div className="font-medium">{t.description?.slice(0, 50) || t.type}</div>
                    <div className="text-xs text-kudi-ink/50">{t.type} · {fmtDate(t.created_at)}</div>
                  </div>
                  <div className="font-semibold tabular-nums">{fmtNaira(t.amount)}</div>
                </div>
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-kudi-ink/70 uppercase tracking-wider mb-2">{title}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-kudi-ink/40 italic">{children}</div>;
}

function Badge({ status }) {
  const map = {
    success: "bg-emerald-50 text-emerald-700",
    pending_admin: "bg-amber-50 text-amber-700",
    failed: "bg-rose-50 text-rose-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${map[status] || "bg-muted text-kudi-ink/60"}`}>{status || "—"}</span>;
}
